import { useState, useEffect } from "react";
import { X, Ticket, MoreHorizontal, ArrowRight } from "lucide-react";
import RedeemCardForm from "./profile/RedeemCardForm";
import MyPayTopupButton from "./profile/MyPayTopupButton";

// Modal launched from Checkout (wallet row) to let the user top-up
// their wallet without leaving the cart.
// Flow:
//  1. Chooser screen: [بكرت شحن] | [خيارات أخرى]
//  2. Selected method renders inline; user can go back.
export default function WalletTopupModal({ open, onClose, onSuccess }) {
  const [choice, setChoice] = useState(null); // null | "card" | "other"

  useEffect(() => {
    if (open) setChoice(null);
  }, [open]);

  if (!open) return null;

  const handleSuccess = (newBalance) => {
    onSuccess?.(newBalance);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="wallet-topup-modal"
    >
      <div
        className="bg-[var(--background)] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            {choice && (
              <button
                onClick={() => setChoice(null)}
                className="p-1.5 rounded-full hover:bg-[var(--surface-alt)] rotate-180"
                data-testid="topup-back-btn"
                aria-label="رجوع"
              >
                <ArrowRight size={18} />
              </button>
            )}
            <div className="font-black text-lg" style={{ fontFamily: "Cairo" }}>
              {choice === "card"
                ? "الشحن بكرت"
                : choice === "other"
                ? "خيارات أخرى للشحن"
                : "شحن المحفظة"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-[var(--surface-alt)]"
            data-testid="topup-close-btn"
            aria-label="إغلاق"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {choice === null && (
            <div className="space-y-3" data-testid="topup-chooser">
              <p className="text-sm text-[var(--text-secondary)] mb-2">
                اختر طريقة شحن محفظتك:
              </p>
              <button
                onClick={() => setChoice("card")}
                className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white border-2 border-[var(--border)] hover:border-primary-brand hover:bg-primary-light transition text-right"
                data-testid="topup-choice-card"
              >
                <div className="w-12 h-12 rounded-2xl bg-primary-light text-primary-brand flex items-center justify-center shrink-0">
                  <Ticket size={22} />
                </div>
                <div className="flex-1">
                  <div className="font-black text-base">شحن عن طريق كرت</div>
                  <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                    أدخل رمز كرت الشحن المكوّن من 13 رقم
                  </div>
                </div>
                <ArrowRight size={18} className="text-[var(--text-secondary)] rotate-180" />
              </button>

              <button
                onClick={() => setChoice("other")}
                className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white border-2 border-[var(--border)] hover:border-primary-brand hover:bg-primary-light transition text-right"
                data-testid="topup-choice-other"
              >
                <div className="w-12 h-12 rounded-2xl bg-primary-light text-primary-brand flex items-center justify-center shrink-0">
                  <MoreHorizontal size={22} />
                </div>
                <div className="flex-1">
                  <div className="font-black text-base">خيارات أخرى</div>
                  <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                    الشحن عن طريق البطاقة المصرفية
                  </div>
                </div>
                <ArrowRight size={18} className="text-[var(--text-secondary)] rotate-180" />
              </button>
            </div>
          )}

          {choice === "card" && (
            <div data-testid="topup-panel-card">
              <RedeemCardForm onSuccess={handleSuccess} />
            </div>
          )}

          {choice === "other" && (
            <div className="space-y-3" data-testid="topup-panel-other">
              <p className="text-xs text-[var(--text-secondary)]">
                طرق الدفع الإلكترونية المتاحة:
              </p>
              <MyPayTopupButton onSuccess={handleSuccess} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
